/**
 * El contrato del cable, espejo de `backend/events.py`.
 *
 * Estos dos archivos son un contrato sin generador entre ambos. Un WebSocket no
 * tiene documento OpenAPI, así que nada te dirá que se desviaron — la trama
 * simplemente cae del switch y la UI deja de actualizarse. Cámbialos en el
 * mismo commit, y mantén las constantes escritas exactamente como las de
 * Python.
 *
 * El sobre es una **unión discriminada sobre `type`**, que no es decoración.
 * La implementación de la que sale esto enviaba un objeto plano y el handler
 * leía `result.document.id` en cada mensaje; el día que se agregó una
 * notificación sin documento, el handler lanzó dentro de `onmessage`. Nada
 * emerge eso: ningún error boundary lo atrapa, el socket sigue abierto, y cada
 * notificación subsiguiente se pierde. Una unión hace que el compilador
 * rechace la forma que lo causó.
 */

/** Sube cuando la forma de una trama cambia de forma incompatible. Ver `Envelope` en Python. */
export const WIRE_VERSION = 1;

export const MESSAGE_READY = 'connection.ready';
export const MESSAGE_CREATED = 'notification.created';
export const MESSAGE_BADGE = 'notification.badge';
export const MESSAGE_PONG = 'pong';
export const MESSAGE_CLOSING = 'connection.closing';

/** Códigos de cierre que el servidor usa a propósito. 1000-2999 son del protocolo. */
export const CLOSE_UNAUTHENTICATED = 4001;
export const CLOSE_SCOPE_REVOKED = 4003;

/**
 * Códigos de catálogo, sembrados — pero no exhaustivos. RR.HH. puede agregar un
 * tipo sin desplegar (ver `NotificationKind`), así que esto es una unión con
 * escape `string` y cada consumidor debe tener una rama por defecto. Tiparlo
 * como unión cerrada sería una mentira que el compilador hace cumplir hasta el
 * día que se rompe.
 */
export type NotificationKindCode = 'informative' | 'task' | 'high' | 'critical' | (string & {});

export interface NotificationDto {
  id: string;
  event_code: string;
  kind: NotificationKindCode;
  title: string;
  body: string;
  target_path: string;
  created_at: string;
  read_at: string | null;
}

export type ServerFrame =
  | { v: number; type: typeof MESSAGE_READY; payload: { unread: number } }
  | {
      v: number;
      type: typeof MESSAGE_CREATED;
      payload: NotificationDto & { unread: number };
    }
  | { v: number; type: typeof MESSAGE_BADGE; payload: { unread: number } }
  | { v: number; type: typeof MESSAGE_PONG; payload: Record<string, never> }
  | { v: number; type: typeof MESSAGE_CLOSING; payload: { reason: string } };

/** Lo que el socket informa de sí mismo, para el único indicador que lo muestra. */
export type SocketStatus = 'connecting' | 'open' | 'reconnecting' | 'closed';
